import GokuDataGrid from '@/components/GokuDataGrid'
import {
    confirmCustomerMailTaskReceived,
    getCustomerMailTasks,
} from '@/modules/Customer/services'
import { useEmailHistoryColumns } from './columns'
import { useTranslation } from 'react-i18next'
import { Title } from 'vnetwork-ui'
import MailIcons from '@/icons/MailIcons'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useState } from 'react'
import { CustomerMailTask } from '@/modules/Customer/types'

interface EmailHistoryProps {
    customerId: number
}

const EmailHistory = ({ customerId }: EmailHistoryProps) => {
    const { t } = useTranslation('customer')
    const { setConfirmation } = useConfirmation()
    const reactQueryKey = `customer-email-history-${customerId}`
    const { refetchCurrent } = useDataGridRefetch({ reactQueryKey })
    const [confirmingTaskId, setConfirmingTaskId] = useState<number | null>(
        null
    )

    const { mutateAsync: confirmReceived } = useMutationWithHandler(
        {
            mutationFn: confirmCustomerMailTaskReceived,
        },
        {
            successMessage: t('mailer.email-history.confirm.success'),
            reactQueryKeys: QUERY_KEYS.CUSTOMERS_MAILERS,
            onSuccess: () => {
                refetchCurrent()
                setConfirmingTaskId(null)
            },
            onError: () => {
                setConfirmingTaskId(null)
            },
        }
    )

    const handleConfirmReceived = (row: CustomerMailTask) => {
        setConfirmation({
            open: true,
            title: t('mailer.email-history.confirm.title'),
            description: t('mailer.email-history.confirm.description', {
                subject: row.mailer_subject || row.mailer_id,
            }),
            onConfirm: async () => {
                setConfirmingTaskId(row.mailer_id)
                await confirmReceived({
                    customerId,
                    taskId: row.mailer_id,
                })
            },
        })
    }

    const columns = useEmailHistoryColumns({
        onConfirmReceived: handleConfirmReceived,
        confirmingTaskId,
    })

    return (
        <div className="bg-white rounded-lg border">
            <GokuDataGrid
                apiCall={(payload) =>
                    getCustomerMailTasks({ ...payload, customerId })
                }
                columns={columns}
                reactQueryKey={reactQueryKey}
                dataGridProps={{
                    getRowId: (row) => row.mailer_id,
                }}
                options={{
                    title: (
                        <Title
                            title={t('mailer.email-history.title')}
                            icon={MailIcons}
                        />
                    ),
                    showHideColumn: false,
                    showSearch: true,
                    pageSize: 10,
                }}
            />
        </div>
    )
}

export default EmailHistory
